import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import api from '../services/api';

const DoctorFormPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Check if we're editing an existing doctor
  const editDoctor = location.state?.editDoctor;
  const isEditMode = !!editDoctor;
  
  // Parse time from "HH:MM" format to hour, minute, period
  const parseTimeForForm = (timeStr) => {
    if (!timeStr) return { hour: '12', minute: '00', period: 'AM' };
    const [hourStr, minuteStr] = timeStr.split(':');
    let hour = parseInt(hourStr);
    const minute = minuteStr || '00';
    let period = 'AM';
    if (hour >= 12) {
      period = 'PM';
      if (hour > 12) hour -= 12;
    } else if (hour === 0) {
      hour = 12;
    }
    return { 
      hour: String(hour).padStart(2, '0'), 
      minute: minute.padStart(2, '0'), 
      period 
    };
  };
  
  // Initialize form with edit data if available
  const getInitialFormData = () => {
    if (editDoctor) {
      const timeData = parseTimeForForm(editDoctor.birth_time);
      return {
        name: editDoctor.name || '',
        dob: editDoctor.dob ? new Date(editDoctor.dob) : null,
        birth_city: editDoctor.birth_city || '',
        birth_time_hour: timeData.hour,
        birth_time_minute: timeData.minute,
        birth_time_period: timeData.period,
      };
    }
    return {
      name: '',
      dob: null,
      birth_city: '',
      birth_time_hour: '12',
      birth_time_minute: '00',
      birth_time_period: 'AM',
    };
  };
  
  const [formData, setFormData] = useState(getInitialFormData);
  const [cities, setCities] = useState([]); // Search results
  const [showCityDropdown, setShowCityDropdown] = useState(false);
  const [cityLoading, setCityLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingV2, setLoadingV2] = useState(false);
  const [error, setError] = useState('');
  const [doctorCount, setDoctorCount] = useState(0);
  const [timeUnknown, setTimeUnknown] = useState(editDoctor ? editDoctor.time_unknown : false);
  const cityInputRef = useRef(null);
  
  // Maximum doctors per user
  const MAX_DOCTORS = 20;

  // Generate hours 1-12
  const hours = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0'));
  // Generate minutes 00-59
  const minutes = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'));

  // Set selectedCity to a dummy object if editing (since we have the city name)
  const [selectedCity, setSelectedCity] = useState(editDoctor ? { name: editDoctor.birth_city } : null);

  // Fetch doctor count on mount (for create mode)
  useEffect(() => {
    if (!isEditMode) {
      const fetchDoctorCount = async () => {
        try {
          const response = await api.get('/doctors/');
          setDoctorCount(response.data?.length || 0);
        } catch (err) {
          console.error('Error fetching doctor count:', err);
        }
      };
      fetchDoctorCount();
    }
  }, [isEditMode]);

  // Search cities via API with debounce
  useEffect(() => {
    if (formData.birth_city.length < 2 || selectedCity) {
      setCities([]);
      return;
    }

    setCityLoading(true);
    const searchCities = async () => {
      try {
        const response = await api.get(`/cities/search?q=${encodeURIComponent(formData.birth_city)}`);
        setCities(response.data.cities || []);
        setShowCityDropdown(true);
      } catch (err) {
        console.error('Error searching cities:', err);
        setCities([]);
      } finally {
        setCityLoading(false);
      }
    };

    const debounce = setTimeout(searchCities, 300);
    return () => clearTimeout(debounce);
  }, [formData.birth_city, selectedCity]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (cityInputRef.current && !cityInputRef.current.contains(event.target)) {
        setShowCityDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCitySelect = (city) => {
    // Show "City, State" unless state is generic "India"
    const displayText = city.state && city.state !== 'India' 
      ? `${city.name}, ${city.state}` 
      : city.name;
    setFormData({ ...formData, birth_city: displayText });
    setSelectedCity(city);
    setShowCityDropdown(false);
    setCities([]);
  };

  const formatTimeForApi = () => {
    let hour = parseInt(formData.birth_time_hour);
    if (formData.birth_time_period === 'PM' && hour !== 12) {
      hour += 12;
    } else if (formData.birth_time_period === 'AM' && hour === 12) {
      hour = 0;
    }
    return `${String(hour).padStart(2, '0')}:${formData.birth_time_minute}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.name.trim()) {
      setError('Please enter the doctor\'s name');
      return;
    }
    if (!formData.dob) {
      setError('Please select the date of birth');
      return;
    }
    if (!formData.birth_city.trim() || !selectedCity) {
      setError('Please select a birth city from the dropdown');
      return;
    }

    setLoading(true);

    try {
      // Format date as YYYY-MM-DD (using local date to avoid timezone issues)
      const year = formData.dob.getFullYear();
      const month = String(formData.dob.getMonth() + 1).padStart(2, '0');
      const day = String(formData.dob.getDate()).padStart(2, '0');
      const dobFormatted = `${year}-${month}-${day}`;
      let birthTime = formatTimeForApi();
      if (timeUnknown) {
        birthTime = '12:00';
      }

      const response = await api.post('/doctors/create-and-generate', {
        name: formData.name.trim(),
        dob: dobFormatted,
        birth_city: formData.birth_city.trim(),
        birth_time: birthTime,
        time_unknown: timeUnknown
      });

      // Navigate to preview with the generated data
      navigate('/preview', { 
        state: { 
          doctor: response.data,
          posterUrl: response.data.poster_url,
          previewUrl: response.data.preview_url
        } 
      });
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to generate poster. Please try again.';
      setError(message);
      setLoading(false);
    }
  };

  const handleSubmitV2 = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.name.trim()) {
      setError('Please enter the doctor\'s name');
      return;
    }
    if (!formData.dob) {
      setError('Please select the date of birth');
      return;
    }
    if (!formData.birth_city.trim() || !selectedCity) {
      setError('Please select a birth city from the dropdown');
      return;
    }

    setLoadingV2(true);

    try {
      // Format date as YYYY-MM-DD (using local date to avoid timezone issues)
      const year = formData.dob.getFullYear();
      const month = String(formData.dob.getMonth() + 1).padStart(2, '0');
      const day = String(formData.dob.getDate()).padStart(2, '0');
      const dobFormatted = `${year}-${month}-${day}`;
      let birthTime = formatTimeForApi();
      if (timeUnknown) {
        birthTime = '12:00';
      }

      const doctorPayload = {
        name: formData.name.trim(),
        dob: dobFormatted,
        birth_city: formData.birth_city.trim(),
        birth_time: birthTime,
        time_unknown: timeUnknown
      };

      let response;
      if (isEditMode) {
        // Update existing doctor and regenerate posters
        response = await api.put(`/doctors/${editDoctor.id}/update-and-regenerate-both`, doctorPayload);
      } else {
        // Create new doctor and generate posters
        response = await api.post('/doctors/create-and-generate-both', doctorPayload);
      }

      // Navigate to preview with both posters
      navigate('/preview', { 
        state: { 
          doctor: response.data,
          // V1 (Star Map) poster
          posterUrlV1: response.data.poster_url_v1,
          previewUrlV1: response.data.preview_url_v1,
          // V6 (Cosmic Profile) poster  
          posterUrlV6: response.data.poster_url_v6,
          previewUrlV6: response.data.preview_url_v6,
          // Legacy/primary (for backward compatibility)
          posterUrl: response.data.poster_url,
          previewUrl: response.data.preview_url,
          version: 'both'
        } 
      });
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to generate posters. Please try again.';
      setError(message);
      setLoadingV2(false);
    }
  };

  return (
    <div className="container">
      {/* Header */}
      <div className="page-header" style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <h1 className="page-title">{isEditMode ? 'Edit Doctor\'s Details' : 'Enter Doctor\'s Details'}</h1>
            <p className="page-subtitle">{isEditMode ? 'Update information and regenerate posters' : 'Please fill in accurate information'}</p>
          </div>
          {!isEditMode && (
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px',
              fontSize: '14px',
              fontWeight: '600'
            }}>
              <span>Submissions:</span>
              <span style={{ 
                backgroundColor: doctorCount >= MAX_DOCTORS ? '#dc3545' : '#1f84f1',
                color: 'white',
                padding: '6px 14px',
                borderRadius: '20px',
                fontSize: '15px'
              }}>
                {doctorCount}/{MAX_DOCTORS}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Show limit reached message */}
      {!isEditMode && doctorCount >= MAX_DOCTORS && (
        <div style={{
          backgroundColor: '#f8d7da',
          color: '#721c24',
          padding: '16px',
          borderRadius: '8px',
          marginBottom: '20px',
          textAlign: 'center'
        }}>
          You have reached the maximum limit of {MAX_DOCTORS} doctors. Please contact admin for assistance.
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Doctor Name with "Dr." prefix */}
        <div className="form-group">
          <label className="form-label">Doctor's Name</label>
          <div className="input-with-prefix">
            <span className="input-prefix">Dr.</span>
            <input
              type="text"
              className="form-input"
              placeholder="Enter full name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            />
          </div>
        </div>

        {/* Date of Birth - Calendar Picker */}
        <div className="form-group">
          <label className="form-label">Doctor's Date of Birth</label>
          <DatePicker
            selected={formData.dob}
            onChange={(date) => setFormData({ ...formData, dob: date })}
            dateFormat="dd/MM/yyyy"
            placeholderText="Select date of birth"
            maxDate={new Date()}
            showYearDropdown
            showMonthDropdown
            dropdownMode="select"
            yearDropdownItemNumber={100}
            scrollableYearDropdown
          />
        </div>

        {/* Birth City - Autocomplete */}
        <div className="form-group">
          <label className="form-label">Doctor's Place of Birth (City)</label>
          <div className="autocomplete-container" ref={cityInputRef}>
            <input
              type="text"
              className="form-input"
              placeholder="Start typing city name..."
              value={formData.birth_city}
              onChange={(e) => {
                setFormData({ ...formData, birth_city: e.target.value });
                setSelectedCity(null); // Reset selection when user types
              }}
              onFocus={() => formData.birth_city.length >= 2 && !selectedCity && setShowCityDropdown(true)}
            />
            {cityLoading && (
              <div className="autocomplete-loading">Searching...</div>
            )}
            {showCityDropdown && cities.length > 0 && !cityLoading && (
              <div className="autocomplete-dropdown">
                {cities.map((city, index) => (
                  <div
                    key={index}
                    className="autocomplete-item"
                    onClick={() => handleCitySelect(city)}
                  >
                    <div className="autocomplete-item-city">{city.name}</div>
                    {city.state && city.state !== 'India' && (
                      <div className="autocomplete-item-state">{city.state}</div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Doctor's Time of Birth</label>
          
          <div style={{ marginBottom: '16px' }}>
            <button
              type="button"
              onClick={() => {
                const newUnknown = !timeUnknown;
                setTimeUnknown(newUnknown);
                if (newUnknown) {
                  setFormData(prev => ({
                    ...prev,
                    birth_time_hour: '12',
                    birth_time_minute: '00',
                    birth_time_period: 'AM',
                  }));
                }
              }}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: timeUnknown ? '#4b5563' : '#f3f4f6',
                color: timeUnknown ? 'white' : '#374151',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '15px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                outline: 'none'
              }}
            >
              {timeUnknown ? (
                <>
                  <span style={{ fontSize: '18px' }}>✓</span>
                  <span>I Don't Know Time</span>
                </>
              ) : (
                <span>I Don't Know Time</span>
              )}
            </button>
          </div>

          <div className="time-picker-container" style={{ 
            opacity: timeUnknown ? 0.4 : 1, 
            pointerEvents: timeUnknown ? 'none' : 'auto',
            transition: 'opacity 0.3s ease',
            filter: timeUnknown ? 'grayscale(100%)' : 'none'
          }}>
            <select
              className="time-select"
              value={formData.birth_time_hour}
              onChange={(e) => setFormData({ ...formData, birth_time_hour: e.target.value })}
            >
              {hours.map((hour) => (
                <option key={hour} value={hour}>{hour}</option>
              ))}
            </select>
            <span style={{ fontSize: '20px', color: '#333' }}>:</span>
            <select
              className="time-select"
              value={formData.birth_time_minute}
              onChange={(e) => setFormData({ ...formData, birth_time_minute: e.target.value })}
            >
              {minutes.map((minute) => (
                <option key={minute} value={minute}>{minute}</option>
              ))}
            </select>
            <select
              className="time-select"
              value={formData.birth_time_period}
              onChange={(e) => setFormData({ ...formData, birth_time_period: e.target.value })}
            >
              <option value="AM">AM</option>
              <option value="PM">PM</option>
            </select>
          </div>
        </div>

        {/* Error message */}
        {error && <p className="error-message">{error}</p>}

        {/* Submit button */}
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleSubmitV2}
          disabled={loading || loadingV2 || (!isEditMode && doctorCount >= MAX_DOCTORS)}
          style={{ marginTop: '24px', opacity: (!isEditMode && doctorCount >= MAX_DOCTORS) ? 0.5 : 1 }}
        >
          {loadingV2 ? (
            <>
              <span className="spinner" style={{ marginRight: '8px', width: '20px', height: '20px' }}></span>
              {isEditMode ? 'Updating & Regenerating...' : 'Generating Cosmic Profile...'}
            </>
          ) : (
            isEditMode ? 'Update & Regenerate ✨' : 'Generate Cosmic Profile ✨'
          )}
        </button>

        {/* Back button */}
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => navigate('/welcome')}
          style={{ marginTop: '12px' }}
          disabled={loading || loadingV2}
        >
          ← Back
        </button>
      </form>

      {/* Loading overlay */}
      {(loading || loadingV2) && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{ 
            width: '60px', 
            height: '60px', 
            border: '4px solid #e0e0e0',
            borderTopColor: loadingV2 ? '#8b5cf6' : '#1f84f1',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          <p style={{ marginTop: '20px', color: '#333', fontWeight: '500' }}>
            {loadingV2 ? 'Creating your V2 Cosmic Profile...' : 'Creating your Cosmic Profile...'}
          </p>
          <p style={{ marginTop: '8px', color: '#666', fontSize: '14px' }}>
            This may take a few moments
          </p>
        </div>
      )}
    </div>
  );
};

export default DoctorFormPage;
